import { Controller } from "@hotwired/stimulus";
import { Turbo } from "@hotwired/turbo-rails";
import { scheduleScrollOpenedCardTitleIntoView } from "../../utils/case_experience/case_experience_in_rails";
import {
  caseExperienceSwitchAdminViewUrl,
} from "../../utils/case_experience/case_experience_api";
import { getCsrfToken } from "../../utils/case_experience/get_csrf_token";

// Connects to data-controller="case-experience--case-preview"

export default class extends Controller {
  static targets = [
    "mobileLessonsOpenButton",
    "mobileLessonsCloseButton",
    "mobileLessonsSidebar",
    "mobileLessonsOverlay",
    "calibrateResultsToggle",
    "calibrateResultsPanel",
    "calibrateResultsIcon",
  ];

  static values = {
    adminPreviewMode: { type: String, default: "ungated" },
    documentId: Number,
  };

  connect() {
    this.adminPreviewRequestInFlight = false;
    this.handleWindowResize = this.handleWindowResize.bind(this);
    this._onAdminViewRowClick = this._onAdminViewRowClick.bind(this);
    window.addEventListener("resize", this.handleWindowResize);
    this.closeMobileLessonsForDesktop();

    this.bindAdminPreviewViewMode();
  }

  disconnect() {
    window.removeEventListener("resize", this.handleWindowResize);
    document.body.classList.remove("overflow-hidden");
    this.unbindAdminPreviewViewMode();
  }

  bindAdminPreviewViewMode() {
    this.adminViewListboxes = Array.from(
      this.element.querySelectorAll("[id='case_experience_student_view_listbox'], [id='case_experience_student_view_mobile_listbox']"),
    );
    this.adminViewListboxes.forEach((listbox) => {
      listbox.addEventListener("click", this._onAdminViewRowClick);
    });
  }

  unbindAdminPreviewViewMode() {
    this.adminViewListboxes?.forEach((listbox) => {
      listbox.removeEventListener("click", this._onAdminViewRowClick);
    });
  }

  _onAdminViewRowClick(event) {
    const row = event.target.closest("[data-railsui-dropdown-menu-target='row']");
    const mode = row?.dataset?.value;
    if (mode) this.onAdminViewModeChange(mode);
  }

  async onAdminViewModeChange(mode) {
    if (!mode || !this.hasDocumentIdValue) return;
    if (mode === this.adminPreviewModeValue) return;
    if (this.adminPreviewRequestInFlight) return;

    this.adminPreviewRequestInFlight = true;
    this.setAdminPreviewControlsDisabled(true);

    try {
      const response = await fetch(caseExperienceSwitchAdminViewUrl(this.documentIdValue), {
        method: "POST",
        headers: {
          Accept: "text/vnd.turbo-stream.html",
          "X-CSRF-Token": getCsrfToken(),
        },
        body: new URLSearchParams({ admin_preview_mode: mode }),
      });

      if (!response.ok) {
        console.error("Failed to switch preview mode:", response.status);
        return;
      }

      const html = await response.text();
      Turbo.renderStreamMessage(html);
      this.adminPreviewModeValue = mode;
      this.element.setAttribute("data-case-experience--case-preview-admin-preview-mode-value", mode);
    } catch (error) {
      console.error("Failed to switch preview mode:", error);
    } finally {
      this.adminPreviewRequestInFlight = false;
      this.setAdminPreviewControlsDisabled(false);
    }
  }

  setAdminPreviewControlsDisabled(disabled) {
    this.adminViewListboxes?.forEach((listbox) => {
      const dropdown = listbox.closest("[data-controller~='railsui-dropdown-menu']") || listbox;
      const controls = dropdown?.querySelectorAll(
        "[data-railsui-dropdown-menu-target~='trigger'], [data-railsui-dropdown-menu-target~='row']",
      );

      controls?.forEach((control) => {
        control.disabled = disabled;
        control.setAttribute("aria-disabled", disabled.toString());
      });
    });
  }

  openMobileLessons() {
    this.setMobileLessonsOpen(true);
  }

  closeMobileLessons() {
    this.setMobileLessonsOpen(false);
  }

  toggleCalibrateResults() {
    const toggle = this.calibrateResultsToggleTarget;
    const panel = this.calibrateResultsPanelTarget;
    const icon = this.hasCalibrateResultsIconTarget ? this.calibrateResultsIconTarget : null;

    if (!toggle || !panel) return;

    const isOpen = toggle.getAttribute("aria-expanded") === "true";

    toggle.setAttribute("aria-expanded", isOpen ? "false" : "true");
    panel.classList.toggle("hidden", isOpen);
    icon?.classList.toggle("rotate-180", !isOpen);
  }

  setMobileLessonsOpen(isOpen) {
    if (
      !this.hasMobileLessonsSidebarTarget ||
      !this.hasMobileLessonsOverlayTarget ||
      !this.hasMobileLessonsOpenButtonTarget
    ) {
      return;
    }

    const isDesktop = window.matchMedia("(min-width: 1024px)").matches;

    // dataset.open drives markup data-[open=true]:*; body overflow-hidden locks scroll while open.
    this.mobileLessonsSidebarTarget.dataset.open = isOpen ? "true" : "false";
    this.mobileLessonsOverlayTarget.dataset.open = isOpen ? "true" : "false";
    this.mobileLessonsSidebarTarget.setAttribute(
      "aria-hidden",
      isOpen || isDesktop ? "false" : "true",
    );
    this.mobileLessonsOpenButtonTarget.setAttribute("aria-expanded", isOpen ? "true" : "false");

    if (this.hasMobileLessonsCloseButtonTarget) {
      this.mobileLessonsCloseButtonTarget.dataset.open = isOpen ? "true" : "false";
    }

    document.body.classList.toggle("overflow-hidden", isOpen);
  }

  handleWindowResize() {
    this.closeMobileLessonsForDesktop();
  }

  closeMobileLessonsForDesktop() {
    // Tailwind lg (1024px): drawer is static in layout; reset JS state if user resized up while open.
    if (window.matchMedia("(min-width: 1024px)").matches) {
      this.setMobileLessonsOpen(false);
    }
  }

  scrollContentPaneOnSectionSwitch(event) {
    const form = event.target;
    const action = form?.getAttribute?.("action") || form?.action || "";
    if (!action.includes("case_experience/switch_section")) return;
    if (event.detail?.success === false) return;
    scheduleScrollOpenedCardTitleIntoView();
  }
}
